import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cn, { Argument as ClassName } from 'classnames'
import { Link } from 'react-router-dom'

import { SLink, SortParameter } from '../../common/features/loadbalancer-group/loadbalancer-groups-widget'
import { WidgetLayout } from '../widget-layout'
import { isAdminDomain, isPartnerDomain } from '../currentDomain'
import Text from '../../ui/Text'
import SettingsInfo from '../../ui/chargepoints/SettingsInfo'
import localizeDate from '../../common/utils/localizeDate'
import { ChargepointStatus } from '../chargepoint-status'
import { ChargersPagination } from '../chargers-pagination'
import { ResultsPerPage } from '../results-per-page'
import {
    PoolGetChargePointsApiResponse,
    useCompanyGetChargePointsPagedQuery,
    useCompanyRemoveChargePointMutation,
} from '../../common/redux/api/web-api'
import { Table, TableHeader, TableBodyCell, TableBody, TableRow, TableHeaderCell } from '../table'
import { STableLink } from '../table/Table.styles'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import DropdownMenu, { DropdownMenuItem } from '../../common/components/DropdownMenu'
import ConfirmationModal from '../../ui/ConfirmationModal'
import TrashIcon from '../icons/trash-icon/trash-icon'
import { Button } from '../../common/components/Button'
import useText from '../../common/hooks/useText'

export type ChargersListWidgetProps = {
    poolChargePoints?: PoolGetChargePointsApiResponse
    companyId?: string
    className?: ClassName
    onAddChargerToPool?: () => void
    style?: React.CSSProperties
}

const SIcon = styled(FontAwesomeIcon)`
    margin-left: 4px;
`

const NoOfItemsWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
`
export const ChargersListWidget = memo(function ChargersListWidget({
    companyId,
    className,
    style,
    poolChargePoints,
    onAddChargerToPool,
}: ChargersListWidgetProps): React.ReactElement {
    const text = useText()
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed?: boolean }>({
        parameter: SortParameter.ID,
    })
    const [paginationData, setPaginationData] = useState<{ NoOfItems?: number; Page?: number }>({})
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false)
    const [selectedChargePointId, setSelectedChargePointId] = useState('')
    const [selectedLoadBalancerGroupId, setSelectedLoadBalancerGroupId] = useState('')

    const { data: chargePoints, isLoading: isLoadingChargePoints } = useCompanyGetChargePointsPagedQuery(
        {
            companyId,
            noOfItems: paginationData.NoOfItems,
            page: paginationData.Page,
            expand: 'additionalInfo,ocppConfig',
        },
        { refetchOnMountOrArgChange: true, skip: !companyId }
    )

    const [removeChargePointMutation, { isLoading: isLoadingRemoveChargePoint }] = useCompanyRemoveChargePointMutation()

    const removeChargePoint = async (chargePointId: string) => {
        try {
            await removeChargePointMutation({
                companyId: companyId,
                chargePointId: chargePointId,
            }).unwrap()
            notifySuccess(text('msgChargerRemoved'))
        } catch (error: unknown) {
            if (error?.status === 404) {
                notifyError(text('msgChargePointNotFound'))
            } else if (error?.status === 409) {
                notifyError(
                    text('msgChargePointNotRemoved', {
                        chargePointId: chargePointId,
                        lbgId: selectedLoadBalancerGroupId,
                    })
                )
            } else if (error?.messageCode) {
                notifyError(text(error?.messageCode))
            } else {
                notifyError(text('lblSomethingWentWrong'))
            }
        } finally {
            setConfirmModalOpen(false)
        }
    }
    return (
        <>
            <WidgetLayout
                className={cn(className)}
                style={style}
                title={text('lblChargersWidget')}
                headerButtons={[
                    isAdminDomain ? (
                        <Button onClick={onAddChargerToPool} size="sm">
                            {text('btnAdd')}
                        </Button>
                    ) : (
                        <></>
                    ),
                ]}
            >
                <br />
                {!isAdminDomain && (
                    <NoOfItemsWrapper>
                        <ResultsPerPage
                            dataTestid="results-per-page"
                            onSelectNumberOfItems={(numberOfItems) =>
                                setPaginationData({ Page: 0, NoOfItems: numberOfItems })
                            }
                        ></ResultsPerPage>
                    </NoOfItemsWrapper>
                )}
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <STableLink
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.ID,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                >
                                    <Text code="lblSerialNumberShort" />
                                    {sortFilter?.parameter === SortParameter.ID && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </STableLink>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblStatus" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblSettings" />
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.LAST_HEARTBEAT,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                >
                                    <Text code="lblLastSeenTime" />
                                    {sortFilter?.parameter === SortParameter.LAST_HEARTBEAT && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.WIFI_SIGNAL_STRENGTH,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                >
                                    <Text code="lblSignal" />
                                    {sortFilter?.parameter === SortParameter.WIFI_SIGNAL_STRENGTH && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.PARTNER_DESCRIPTION,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                >
                                    <Text code="lblPartnerInfo" />
                                    {sortFilter?.parameter === SortParameter.PARTNER_DESCRIPTION && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.NAME,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                >
                                    <Text code="lblName" />
                                    {sortFilter?.parameter === SortParameter.NAME && (
                                        <SIcon icon={sortFilter.reversed ? faCaretUp : faCaretDown} />
                                    )}
                                </a>
                            </TableHeaderCell>
                            {isPartnerDomain && (
                                <TableHeaderCell data-testid="table-chargepointremove-heading"></TableHeaderCell>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!isLoadingChargePoints &&
                            !isAdminDomain &&
                            chargePoints?.items.map((chargePoint, index) => (
                                <TableRow key={index}>
                                    <TableBodyCell>
                                        <SLink as={Link} to={`/chargepoints/${chargePoint.id}`}>
                                            {chargePoint.id}
                                        </SLink>
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <ChargepointStatus chargePoint={chargePoint} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <SettingsInfo chargePoint={chargePoint} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        {(chargePoint.chargePointStatus === 'Offline' &&
                                            localizeDate(chargePoint?.lastHeartbeat)) ||
                                            '---'}
                                    </TableBodyCell>
                                    <TableBodyCell>{chargePoint?.wifiSignalStrength || '---'}</TableBodyCell>
                                    <TableBodyCell>
                                        {chargePoint?.additionalInfo?.partnerDescription || '---'}
                                    </TableBodyCell>
                                    <TableBodyCell className="hide-overflow">
                                        {chargePoint?.name || '---'}
                                    </TableBodyCell>
                                    {isPartnerDomain && (
                                        <TableBodyCell data-testid="table-userremoveBtn-body">
                                            <DropdownMenu className="ml-auto" data-testid="dropdown-menu-btn">
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        setConfirmModalOpen(true)
                                                        setSelectedChargePointId(chargePoint.id)
                                                        setSelectedLoadBalancerGroupId(chargePoint.loadBalancerGroupId)
                                                        e.stopPropagation()
                                                    }}
                                                    disabled={!!chargePoint.loadBalancerGroupId}
                                                    disabledReason={
                                                        chargePoint.loadBalancerGroupId
                                                            ? text('msgChargePointNotRemoved', {
                                                                  chargePointId: chargePoint.id,
                                                                  lbgId: chargePoint.loadBalancerGroupId,
                                                              })
                                                            : undefined
                                                    }
                                                    text={text('btnRemove')}
                                                    icon={<TrashIcon size={16} />}
                                                    key="btnRemoveCharger"
                                                    data-testid="btn-remove-charger"
                                                />
                                            </DropdownMenu>
                                        </TableBodyCell>
                                    )}
                                </TableRow>
                            ))}

                        {isAdminDomain &&
                            poolChargePoints?.map((chargePoint, index) => (
                                <TableRow key={index}>
                                    <TableBodyCell>
                                        <SLink
                                            as={Link}
                                            to={`/chargepoints/${chargePoint.id}`}
                                            className="btn btn-link btn-xs"
                                            data-testid={`table-tr-serial-${chargePoint.id}-btn`}
                                        >
                                            {chargePoint.id}
                                        </SLink>
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <ChargepointStatus chargePoint={chargePoint || ''} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <SettingsInfo chargePoint={chargePoint || ''} />
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        {(chargePoint?.chargePointStatus === 'Offline' &&
                                            localizeDate(chargePoint?.lastHeartbeat || '')) ||
                                            '---'}
                                    </TableBodyCell>
                                    <TableBodyCell>{chargePoint?.wifiSignalStrength || ''}</TableBodyCell>
                                    <TableBodyCell>
                                        {chargePoint?.additionalInfo?.partnerDescription || ''}
                                    </TableBodyCell>
                                    <TableBodyCell className="hide-overflow">{chargePoint?.name || ''}</TableBodyCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                {(isLoadingChargePoints || isLoadingRemoveChargePoint) && (
                    <div className="shimmer-container shimmer-small" />
                )}
                {chargePoints && (
                    <ChargersPagination
                        totalCount={chargePoints?.totalCount}
                        pageIndex={chargePoints.pageIndex}
                        pageSize={chargePoints.pageSize}
                        displayedChargers={chargePoints.items.length}
                        setPageIndex={(index) => {
                            setPaginationData({ ...paginationData, Page: index })
                        }}
                    />
                )}
            </WidgetLayout>
            {isConfirmModalOpen && (
                <ConfirmationModal
                    message={text('msgConfirmChargerRemoval')}
                    onClose={() => setConfirmModalOpen(false)}
                    onConfirm={() => removeChargePoint(selectedChargePointId)}
                />
            )}
        </>
    )
})
